/**
 * Type Definitions - holito Testimonial Platform
 * 
 * Central type definitions shared across the application.
 * Includes types for:
 * - Interview sessions and data
 * - Conversation turns and quotes
 * - Research events
 * - Case study analysis
 * - Room access and authentication
 * 
 * @module types
 */

import type { FileCollectionConstraints } from './lib/integrations/storage/types';
import type { ChoiceLibraryId } from './lib/choice-library';
import type { QuestionImage } from './lib/question-blocks';

// ============================================================================
// APPLICATION STATE & CORE TYPES
// ============================================================================

import type { CalendarBookingRecord } from './lib/integrations/calendar/types';

/** Interview flow states */
export enum AppState {
  Setup,
  PreInterview,
  ClientIntro,
  InCall,
  PostCallIdentity,
  Success,
}

export interface VerifiedQuote {
  text: string;
  timestamp: number;
}

export interface ConversationTurn {
  speaker: 'user' | 'ai' | 'system-prompt' | 'system-confirmation' | 'system-image-generation' | 'system-quote-saved';
  text: string;
  timestamp?: number;
  functionCallId?: string;
  /**
   * Stable identity minted (crypto.randomUUID) when the turn is created in the
   * live-session worker. Absent on turns recorded before anchor capture
   * shipped. The canonical turn key everywhere is
   * `turn.turn_id ?? 'ts:' + turn.timestamp`.
   */
  turn_id?: string;
  /** Wall clock (epoch ms) when the turn was finalized in the worker. */
  finalized_at?: number;
  /**
   * True when an AI turn was finalized because the user interrupted, meaning
   * the trailing transcript words were never spoken aloud.
   */
  interrupted?: boolean;
}

/** Real-time research event during interview */
export interface ResearchEvent {
  id: string;
  type: 'primary' | 'background';
  query: string;
  status: 'searching' | 'summarizing' | 'complete';
  summary?: string;
  timestamp: number;
}

// ============================================================================
// PRODUCT RESEARCH & INTERVIEW CONTEXT
// ============================================================================

/** AI-generated product/service research from URL analysis */
export interface ProductResearch {
  summary: string;
  keyFeatures?: string[];
  targetAudience?: string;
  valueProposition?: string;
  brandVoiceTone?: string;
  crawledPageCount?: number;
  searchSources?: { url: string; title: string }[];
  researched_at: string;
}

/** Interview context passed during session creation and call setup */
export interface InterviewContext {
  targetName: string;
  targetUrl?: string;
  goal: string;
  details: string;
  productResearch?: ProductResearch;
  brandId?: string;
  intervieweeName?: string;
  intervieweeEmail?: string;
  intervieweeJob?: string;
  intervieweeCompany?: string;
  intervieweeLinkedin?: string;
  intervieweeCompanyUrl?: string;
  /** BCP-47 language code for conducting the interview (e.g., 'en-US', 'es-ES') */
  interviewLanguage?: string;
  /** BCP-47 language code the manager wants to receive the transcript in (e.g., 'en-US') */
  outputLanguage?: string;
  /**
   * Whether the client platform supports getDisplayMedia. False on iOS
   * (method undefined) and Android (method exists but always rejects -
   * crbug 487935). Worker ANDs this with the campaign DB flag before
   * declaring screen-share tools to Gemini, so the AI doesn't call
   * request_screen_share when the client physically cannot act on it.
   * Absence of the field = old client, assume supported.
   */
  clientSupportsScreenShare?: boolean;
  /**
   * IANA timezone of the respondent's browser
   * (Intl.DateTimeFormat().resolvedOptions().timeZone). Client-truth data
   * like screen-share capability: the worker validates it and uses it to
   * present calendar slots in the respondent's local time, falling back to
   * the host's calendar timezone when absent or invalid.
   */
  respondentTimezone?: string;
  /**
   * Number of admin-designed form fields on the campaign. Sourced server-
   * side from `interview_sessions.form_schema.length`; the schema array
   * itself never leaves the server, so the question content is not
   * disclosed. Used by the join screen to render a math-backed time
   * estimate via {@link lib/estimate-duration.formatEstimatedDuration}.
   */
  fieldCount?: number;
  /**
   * Campaign probing depth (1 Surface - 4 Deep). Drives the per-question
   * time coefficient in the join-screen estimate. Defaults to 3
   * (Balanced) when the column is null.
   */
  probingDepth?: number;
}


// ============================================================================
// DATABASE TYPES - Supabase Tables
// ============================================================================

export type InterviewSessionStatus = 'setup' | 'active' | 'completed' | 'expired';

export interface InterviewSession {
  id: string;
  created_by: string;
  /** Owning organization (null for legacy pre-org sessions). */
  organization_id?: string | null;
  room_id: string;
  passcode?: string; // Not returned from DB (hashed), only used for display after creation
  interview_link_hash: string;
  status: InterviewSessionStatus;
  // Interview configuration (direct columns)
  target_name: string;
  target_url?: string;
  goal: string;
  details?: string;
  /**
   * Admin-designed FormField[] that defines the structure of the interview.
   * Optional because rows created before the form-first redesign do not have
   * this column populated; the DB default is `[]`. Phase 1 derives `details`
   * from this array so the live-session worker keeps working unchanged.
   */
  form_schema?: FormField[];
  /** Participation settings, mandatory for saved definitions after the upgrade migration. */
  conversation_config?: import('./lib/multimodal-contract').ConversationExperienceConfig | null;
  product_research?: ProductResearch;
  output_language?: string;
  brand_id?: string;
  /** Whether video recording is enabled for this interview session (Pro feature) */
  video_enabled: boolean;
  /** Maximum number of responses allowed (null = unlimited) */
  max_responses?: number | null;
  /** Whether duplicate emails are allowed across responses */
  allow_duplicate_emails?: boolean;
  /** Whether respondent name is required */
  require_name?: boolean;
  /** Whether respondent email is required */
  require_email?: boolean;
  /** When true, camera is mandatory: no audio-only fallback offered */
  require_video?: boolean;
  /** When true, hollie may request the respondent's screen and the respondent may volunteer it. Requires video_enabled. */
  screen_share_enabled?: boolean;
  /** Enforced interview language code (e.g., 'en', 'fr'). When set, all respondents interview in this language. */
  enforce_language?: string | null;
  /**
   * How aggressively hollie probes for detail during the call
   * (1 Surface - 4 Deep). NULL defaults to 3 (Balanced) server-side.
   */
  probing_depth?: number;
  /** When the campaign opens for responses (null = immediately open) */
  opens_at?: string | null;
  /** When the campaign closes for responses (null = no auto-close) */
  closes_at?: string | null;
  /** When the aggregated campaign report was last generated */
  aggregated_report_generated_at?: string;
  /** Count of per-response reports included in the last aggregation */
  aggregated_report_included_count?: number;
  /**
   * When and how the campaign collects respondent identity. Drives
   * PreInterviewForm skipping (post, never) and the post-call identity
   * step (post). Same value applies to direct-link and embed flows.
   */
  identity_collection?: 'pre' | 'post' | 'never';
  /**
   * Origins (full origin including scheme) allowed to embed this campaign
   * via the parent-page snippet. When non-empty, the iframe-side route
   * checks `embed_origin` against this list and refuses to render when
   * it does not match.
   */
  allowed_origins?: string[] | null;
  /** Action taken when a respondent finishes the call (Business plan). */
  completion_action?: 'message' | 'redirect';
  /** Custom thank-you message shown when completion_action is 'message'. */
  completion_message?: string | null;
  /** Destination URL for completion_action = 'redirect'. */
  completion_redirect_url?: string | null;
  created_at: string;
  updated_at: string;
  expires_at?: string;
}

/** Video segment processing status */
export type VideoSegmentStatus =
  | 'uploading'  // Initial upload in progress
  | 'complete'   // Upload complete, ready for processing
  | 'processing' // Currently being processed by Stream
  | 'processed'  // Successfully processed and available
  | 'failed';    // Processing failed permanently

/**
 * Which recorder produced a segment. Camera, screen-share, and audio-only each
 * use independent `segment_index` counters, so admin UI + API endpoints must
 * key on both `segment_kind` and `segment_index` to avoid one playing back as
 * the other. `'audio'` segments are produced by audio-only calls (no camera);
 * they bypass the Cloudflare Stream pipeline and are served directly from R2.
 */
export type SegmentKind = 'camera' | 'screen' | 'audio';

/** Video segment information stored in the database */
export interface VideoSegment {
  /** Zero-based index of the segment within the session */
  segment_index: number;
  /** Path to the video file in R2 */
  r2_path: string;
  /** Start time of segment in milliseconds from session start */
  start_ms: number;
  /** End time of segment in milliseconds from session start */
  end_ms: number;
  /** File size in bytes */
  size_bytes: number;
  /** Current processing status */
  status: VideoSegmentStatus;
  /** MIME type of the video file */
  mime_type: string;
  /** ISO timestamp when processing completed */
  processed_at?: string;
  /** Path to raw file before processing (for reference after deletion) */
  original_r2_path?: string;
  /** Cloudflare Stream video UID (during processing) */
  stream_uid?: string;
  /** Error message if processing failed */
  error_message?: string;
  /** ISO timestamp when segment failed */
  failed_at?: string;
  /** Number of processing retry attempts */
  retry_count?: number;
  /** ISO timestamp of last retry attempt */
  last_retry_at?: string;
  /** Path to thumbnail image in R2 */
  thumbnail_path?: string;
  /** Video duration in seconds (from Stream metadata) */
  duration_seconds?: number;
  /** Video dimensions from Stream metadata */
  dimensions?: {
    width: number;
    height: number;
  };
  /**
   * Recorder that produced the segment. Defaults to 'camera' for rows written
   * before screen-share support landed.
   */
  segment_kind?: SegmentKind;
  /**
   * Client `Date.now()` captured in `MediaRecorder.onstart` and reported over
   * the socket. Removes the upload-open bias (roughly 2 seconds of
   * MediaRecorder timeslice buffering) from segment start estimates. Absent
   * on segments recorded before anchor capture shipped.
   */
  client_start_ms?: number;
  /**
   * Worker-estimated client-minus-server clock offset in ms, derived from the
   * ping/pong heartbeat (lowest-RTT sample). Stamped at segment finalize with
   * the best estimate known by then. Absent when no extended pong arrived.
   */
  clock_offset_ms?: number;
}

/**
 * One word of a transcript timing row: word text, start/end in ms relative to
 * the home segment's media clock, and whether the word matched ASR output
 * (`m: true` words are alignment ground truth; `m: false` are interpolated).
 */
export interface TranscriptTimingWord {
  /** Word text as rendered. */
  w: string;
  /** Word start, media-relative ms. */
  s: number;
  /** Word end, media-relative ms. */
  e: number;
  /** True when the word matched ASR output during forced alignment. */
  m: boolean;
}

/**
 * Row of the `transcript_timings` table: word-accurate timing for one
 * conversation turn, media-relative to its home video segment. Written by the
 * Stage 2 alignment pipeline (service role only); read by the admin UI via
 * RLS mirroring `video_segments_select`. One row per
 * `(interview_data_id, turn_key)`.
 */
export interface TranscriptTiming {
  id: string;
  interview_data_id: string;
  /** `turn.turn_id` when present, else `ts:<turn.timestamp>`. */
  turn_key: string;
  /** Copy of the turn speaker at alignment time. */
  speaker: string;
  /** Home segment row id when known (FK, set null on segment delete). */
  segment_id: string | null;
  /** 'camera' or 'audio'; screen segments never host timings. */
  segment_kind: string;
  /** With `segment_kind`, joins against JSONB-sourced segments in the UI. */
  segment_index: number;
  /** Utterance start, media-relative to the home segment (ms). */
  turn_start_ms: number;
  /** Utterance end, media-relative to the home segment (ms). */
  turn_end_ms: number;
  /**
   * Spoken words with timings; length equals `spoken_word_count`, so an
   * interrupted tail is simply absent.
   */
  words: TranscriptTimingWord[];
  /** Words actually spoken; less than the turn's word count after an interruption. */
  spoken_word_count: number;
  /** 'aligned' (ASR-matched) or 'interpolated' (anchor fallback). */
  timing_source: 'aligned' | 'interpolated';
  /** Fraction of turn words with `m: true`. */
  match_ratio: number;
  /** Bumped to force re-alignment after algorithm changes. */
  alignment_version: number;
  created_at: string;
  updated_at: string;
}

/** Response status for multi-response campaigns */
export type ResponseStatus = 'pending' | 'in_progress' | 'completed' | 'low_quality' | 'abandoned' | 'insufficient';

/** Session-latched conversation engine rollout mode. */
export type ConversationEngineMode = 'legacy' | 'shadow' | 'active';

/** Persisted state of one authored conversation step. */
export type ConversationProgressStepState = 'pending' | 'active' | 'completed' | 'skipped' | 'failed';

/** Bounded semantic outcomes accepted by the progress reducer. */
export type ConversationProgressSemanticOutcome =
  | 'answered'
  | 'partial'
  | 'uncertain'
  | 'declined'
  | 'unable'
  | 'off_topic'
  | 'interrupted'
  | 'invalid'
  | 'requested_end';

/** Known authored field types plus an explicit compiler fallback. */
export type ConversationProgressFieldType = FieldType | 'unsupported';

/**
 * Read-safe persisted projection of one compiled conversation step.
 * Runtime documents may add private counters and lifecycle facts without
 * changing this admin contract.
 */
export interface ConversationProgressStep {
  /** Stable schema field identity emitted by the core reducer. */
  fieldId?: string;
  /** Legacy compiled identity retained only for staggered rollout reads. */
  stepId?: string;
  /** Programmatic field key from the response schema snapshot. */
  fieldName: string;
  /** Authored field type, or unsupported when compilation failed safely. */
  fieldType: ConversationProgressFieldType;
  /** Zero-based authored position in the immutable response schema. */
  schemaPosition: number;
  /** Sanitized admin-facing label stored with the progress document. */
  displayLabel: string;
  /** Current reducer-owned state. */
  state: ConversationProgressStepState;
  /** Model-classified outcome accepted by the reducer, when available. */
  semanticOutcome?: ConversationProgressSemanticOutcome;
  /** Stable transcript turn keys supporting this step's outcome. */
  evidenceTurnIds: string[];
  /** Machine-readable or safe display reason for a skipped step. */
  skipReason?: string;
  /** Machine-readable or safe display reason for a failed step. */
  failureReason?: string;
  /** Passive information is presented without increasing question progress. */
  countsTowardsProgress?: boolean;
  /** Whether skipping this step prevents completion. */
  required?: boolean;
  /** Canonical reducer reason used by the persisted runtime document. */
  reasonCode?: string;
}

/** Durable, versioned admin read model for schema-ordered conversation progress. */
export interface ConversationProgressDocument {
  /** Read-side receipt metadata, validated by response-completion before display. */
  multimodal?: unknown;
  /** Persisted document format version. */
  formatVersion: number;
  /** Fingerprint of the immutable response schema snapshot. */
  schemaFingerprint: string;
  /** Session-latched rollout mode used by this response. */
  mode: ConversationEngineMode;
  /** Zero-based active step index, or null after terminal completion. */
  activeStepIndex: number | null;
  /** Monotonic compare-and-set revision. */
  revision: number;
  /** ISO timestamp when progress was initialized. */
  createdAt: string;
  /** ISO timestamp of the latest accepted transition. */
  updatedAt: string;
  /** One state record per compiled step. */
  steps: ConversationProgressStep[];
}

export interface InterviewData {
  id: string;
  interview_session_id: string;
  /** Response number within the campaign (1-based) */
  response_number?: number;
  /** Status of this individual response */
  response_status?: ResponseStatus;
  conversation_turns: ConversationTurn[];
  research_events: ResearchEvent[];
  verified_quotes: VerifiedQuote[];
  completed_at?: string;
  created_at: string;
  updated_at: string;
  /** Original language code of the interview (BCP-47) */
  original_language?: string;
  /** Translated conversation turns (if different from original) */
  translated_turns?: ConversationTurn[];
  /** Language code of the translation (BCP-47) */
  translated_language?: string;
  /** When the translation was performed */
  translated_at?: string;
  /** User report feedback text */
  feedback?: string;
  /** User rating (1-5) */
  rating?: number;
  /** Active session segments for duration tracking */
  duration_segments?: { start_ms: number; end_ms: number }[];
  /** Video segments for recorded interviews */
  video_segments?: VideoSegment[];
  // Interviewee information (direct columns)
  interviewee_name?: string;
  interviewee_email?: string;
  /** Confirmed bookings, including the guest's invite address. */
  calendar_bookings?: CalendarBookingRecord[] | null;
  interviewee_job?: string;
  interviewee_company?: string;
  interviewee_linkedin?: string;
  interviewee_company_url?: string;
  /** Arbitrary admin-defined metadata key-value pairs (e.g., customer_id, subscription_plan) */
  interviewee_metadata?: Record<string, string>;
  /**
   * Structured answers keyed by `FormField.name`. Populated by the
   * `submit_structured_answer` tool during the live interview. Values are
   * intentionally typed as `unknown` because they vary by field type (string
   * for text/email, number for numeric, string|string[] for choice).
   * Optional because rows created before the form-first redesign do not have
   * this column populated; the DB default is `{}`.
   */
  form_answers?: Record<string, unknown>;
  /** Immutable schema captured when this response was created. */
  form_schema_snapshot?: FormField[] | null;
  /** Immutable authoring settings, including section titles and memberships. */
  conversation_config_snapshot?: import('./lib/multimodal-contract').ConversationExperienceConfig | null;
  /** Durable state-machine read model used by workers and the admin UI. */
  conversation_progress?: ConversationProgressDocument | null;
  /** Database-level progress format version for compatibility checks. */
  conversation_progress_version?: number | null;
  /** Database compare-and-set revision mirrored by the progress document. */
  conversation_progress_revision?: number | string | null;
  /** Deterministic fingerprint of the response schema snapshot. */
  schema_fingerprint?: string | null;
  /** Session-latched progressive-engine rollout mode. */
  conversation_engine_mode?: ConversationEngineMode | null;
}

// ============================================================================
// STORAGE & ACCESS TYPES
// ============================================================================

/** Validated room access response */
export interface RoomAccess {
  sessionId: string;
  roomId: string;
  /** Eligible versioned experience; the response bootstrap remains authoritative. */
  conversation_config?: import('./lib/multimodal-contract').ConversationExperienceConfig | null;
  status: InterviewSessionStatus;
  // Interview configuration from session
  target_name: string;
  target_url?: string;
  goal: string;
  details?: string;
  product_research?: ProductResearch;
  output_language?: string;
  /** Persisted interview language from resumed sessions */
  interview_language?: string;
  videoEnabled: boolean;
  /** When true, camera is mandatory: no audio-only fallback offered */
  requireVideo?: boolean;
  /** When true, hollie may request the respondent's screen and the respondent may volunteer it. Only effective with videoEnabled. */
  screenShareEnabled?: boolean;
  // Campaign settings for multi-response
  /** Whether respondent name is required */
  require_name?: boolean;
  /** Whether respondent email is required */
  require_email?: boolean;
  /** Enforced interview language code (e.g., 'en', 'fr'). When set, overrides respondent's language choice. */
  enforce_language?: string | null;
  // Schedule status for campaign open/close windows
  /** Schedule status: 'open', 'not_yet_open', or 'closed' */
  schedule_status?: 'open' | 'not_yet_open' | 'closed';
  /** When the campaign opens for responses */
  opens_at?: string | null;
  /** When the campaign closes for responses */
  closes_at?: string | null;
  /** Whether to show holito referral CTA on the success screen. Defaults to true when absent. */
  show_holito_referral?: boolean;
  /**
   * Workspace (brand) ID owning the session, when the session is linked to one.
   * Forwarded from the room validate / status / resume endpoints so the live
   * interview surfaces can render workspace branding (wide logo on the join +
   * on-call header, square icon on loaders).
   */
  workspaceId?: string | null;
  /**
   * Relative URL serving the workspace's wide logo
   * (`/api/workspace-assets/{id}/logo`). Null when the workspace has no logo
   * uploaded - surfaces should fall back to the holito BETA mark in that case.
   */
  workspaceLogoUrl?: string | null;
  /**
   * Relative URL serving the workspace's square icon
   * (`/api/workspace-assets/{id}/icon`). Kept on the contract for legacy /
   * favicon-style usage. Null when no icon is uploaded.
   */
  workspaceIconUrl?: string | null;
  /** Space accent applied to respondent controls and related colors. */
  workspaceAccentColor?: string | null;
  /**
   * Number of admin-designed form fields on the campaign. Surfaced for the
   * join-screen time estimate. The raw `form_schema` array stays on the
   * server, only the count crosses the wire.
   */
  field_count?: number;
  /**
   * Campaign probing depth (1 Surface - 4 Deep). Drives the per-question
   * coefficient in the join-screen time estimate.
   */
  probing_depth?: number;
  /**
   * Whether the workspace's plan grants the embed_widget feature. Used by
   * the /embed/:hash route to refuse rendering when the workspace cannot
   * use the embed surface. UI hides the snippet upstream too; this is the
   * iframe-side belt-and-braces check.
   */
  embed_widget?: boolean;
  /**
   * When and how the campaign collects respondent identity. 'pre' shows
   * the pre-call form, 'post' defers to the post-call identity step,
   * 'never' skips identity entirely. Drives both direct-link and embed
   * flows; defaults to 'pre' when the validate endpoint omits the field.
   */
  identity_collection?: 'pre' | 'post' | 'never';
  /**
   * Origins permitted to embed this campaign via the parent-page snippet.
   * NULL or empty array means "embeddable anywhere" (no restriction).
   * When non-empty, the iframe-side launcher and join shell refuse to
   * render unless the parent origin matches an entry. Mirrors the column
   * on `interview_sessions.allowed_origins`.
   */
  allowed_origins?: string[] | null;
  /** Post-call behaviour. 'redirect' is gated by completion_redirect entitlement. */
  completion_action?: 'message' | 'redirect';
  /** Custom success-screen body (NULL/blank → default copy). */
  completion_message?: string | null;
  /** Destination URL when completion_action is 'redirect'. */
  completion_redirect_url?: string | null;
  // BP-6: interviewee fields are per-response (on InterviewData), not returned from room validation
}

/** Generated shareable interview link */
export interface GeneratedLink {
  roomId: string;
  passcode: string;
  hash: string;
  fullUrl: string;
}

/**
 * Brand-aware boot payload set on `window.__roomBoot` by the
 * `functions/join/[hash].ts` Pages Function. Lets the boot loader paint
 * the workspace logo on the first frame and lets `RoomJoinPage` skip the
 * initial `/api/room/status` round-trip.
 *
 * **Never trust this for auth.** It contains only public-by-design data
 * (the same shape `/api/room/status` already returns). Bcrypt passcode
 * validation still runs through `/api/room/validate`.
 */
export interface RoomBoot {
  /** 8-char roomId decoded from the join link hash. */
  roomId: string;
  /** Subject company name shown on the join page. May be null. */
  targetName: string | null;
  /** Workspace (brand) ID owning the room, or null when unbranded. */
  workspaceId: string | null;
  /** Wide logo URL for the join + on-call header (`/api/workspace-assets/{id}/logo`). */
  workspaceLogoUrl: string | null;
  /** Square icon URL (`/api/workspace-assets/{id}/icon`). */
  workspaceIconUrl: string | null;
  /** Space accent for the first respondent paint. Older boot payloads may omit it. */
  workspaceAccentColor?: string | null;
  /** Schedule status at the moment the SSR function ran. */
  scheduleStatus: 'open' | 'not_yet_open' | 'closed';
  /** ISO timestamp the campaign opens, or null. */
  opensAt: string | null;
  /** ISO timestamp the campaign closes, or null. */
  closesAt: string | null;
}

declare global {
  interface Window {
    /** Boot epoch (performance.now() when the inline boot loader rendered). */
    __bootEpochMs?: number;
    /** Brand-aware boot payload from `functions/join/[hash].ts`. */
    __roomBoot?: RoomBoot;
  }
}

// ============================================================================
// FORM FIELD TYPES - Form-First Interview Redesign
// ============================================================================
//
// The `FormField` discriminated union is the structural source of truth for an
// interview. It replaces the freeform `goal` + `details` blob authored in the
// legacy "Key Themes" wizard step. Each field is either a Free Form Explore
// prompt (hollie riffs conversationally) or a structured ask. A per-field
// `mustOrTry` toggle controls whether hollie hits the field in builder order
// ("must") or weaves it in opportunistically ("try"). In Phase 1 every field
// is stamped `'try'` (the toggle is hidden) so respondent-facing behaviour is
// unchanged from the freeform interview that ships today.
//
// Naming note: we deliberately use snake_case for `FieldType` literals
// (`free_form_explore`, `choice_single`) to match this codebase's backend
// column-naming conventions. Unicorn's form-builder uses kebab-case
// (`free-form-explore`); we did not preserve that casing.

/**
 * Stored field types for the typed conversation builder, including
 * customer-owned file collection.
 * URL is an answer format backed by `short_text` with `textFormat: 'url'`.
 * Conditional visibility uses accepted answers; page breaks are not supported.
 */
export type FieldType =
  | 'free_form_explore'
  | 'information'
  | 'calendar_booking'
  | 'file_upload'
  | 'short_text'
  | 'number'
  | 'email'
  | 'phone'
  | 'name'
  | 'choice_single'
  | 'choice_multi'
  | 'checkbox'
  | 'image_choice'
  | 'ranking'
  | 'date_time'
  | 'matrix'
  | 'address'
  | 'signature'
  | 'image_click_map';

/**
 * Per-field directive for hollie:
 * - `'must'`: collect this answer in builder order before any `'try'` field.
 * - `'try'`: weave the topic in opportunistically; may be skipped or reordered.
 *
 * Phase 1 hides the toggle and stamps every field `'try'`. Phase 2 reveals it
 * and the live-session worker honours it via the
 * `<must_cover_questions>` / `<try_to_cover_questions>` prompt blocks.
 */
export type MustOrTry = 'must' | 'try';

/**
 * A single selectable option for `choice_single` / `choice_multi` fields.
 *
 * @property id    Stable client-generated UUID. Used as React key and for
 *                 reordering without relying on label/value.
 * @property label User-facing option text the admin authors.
 * @property value Programmatic value sent to the backend in `form_answers`.
 *                 May equal `label` initially; admins can edit independently.
 */
export interface FormFieldOption {
  id: string;
  label: string;
  value: string;
  /** Optional uploaded visual used by image-choice questions. */
  image?: QuestionImage;
}

/** Stable identity and respondent-facing label for a matrix axis item. */
export interface FormFieldMatrixItem {
  id: string;
  label: string;
}

/**
 * Fields shared by every variant in the `FormField` discriminated union.
 * The `type` discriminator lives on the per-type config, not here, so TS can
 * narrow correctly when callers switch on `field.type`.
 *
 * @property id          Stable client-generated UUID. Used as React key, drag
 *                       reorder identity, and structured-answer correlation.
 * @property name        Programmatic key (snake_case). Used as the key in
 *                       `interview_data.form_answers`. Must be unique within a
 *                       conversation. Lowercase + underscores + digits only.
 * @property label       Authored label shown to the respondent: a concise fact
 *                       label or a question when the answer needs context.
 * @property helpText    Optional useful clarification shown with the label to
 *                       the respondent; may also inform hollie's context.
 * @property required    Whether the field is required. Distinct from
 *                       `mustOrTry`: `required` is a validation rule on the
 *                       answer; `mustOrTry` controls hollie's coverage strategy.
 * @property mustOrTry   See `MustOrTry`.
 * @property placeholder Optional placeholder text for input rendering.
 */
export interface FormFieldBase {
  /** Ask only when validated earlier answers match this rule. */
  showWhen?: import('./lib/question-conditions').QuestionCondition;
  id: string;
  name: string;
  label: string;
  helpText?: string;
  required: boolean;
  mustOrTry: MustOrTry;
  placeholder?: string;
  /** Accepted answer input; absent preserves support for any input. */
  answerChannel?: 'any' | 'on_screen';
  /** Earlier Information field kept visible while collecting this answer. */
  informationFieldId?: string;
}

/** Versioned rating semantics, independent from its backing value type. */
export interface FormFieldRating {
  version: 1;
  preset: 'nps' | 'scale' | 'faces';
  lowLabel: string;
  highLabel: string;
  direction: 'ascending' | 'descending';
}

/**
 * Free Form Explore: hollie riffs conversationally on a topic. The closest
 * analogue to today's freeform interview behaviour. The optional `topic`
 * narrows the exploration; without it, hollie uses `label` directly.
 */
export interface FreeFormExploreFieldConfig {
  type: 'free_form_explore';
  /** One-sentence hint about what hollie should explore. Defaults to `label`. */
  topic?: string;
  /** What this question should uncover, private to hollie. */
  goal?: string;
  /** Follow-up depth for this question in text and calls. Defaults to Balanced. */
  probingDepth?: 2 | 3 | 4;
}

/**
 * Information block: text or an image shown to the respondent, optionally read
 * aloud during a call. It collects no answer; a linked question can ask for one.
 *
 * The image bytes never reach the live voice worker. At authoring time an AI
 * `caption` is generated and stored: during the call hollie reasons about the
 * image only through that caption, while the respondent's browser fetches and
 * renders the image directly from `imageUrl`. The caption is therefore
 * load-bearing realtime data, not a nice-to-have, and publish is gated on it.
 */
export interface InformationBlockFieldConfig {
  type: 'information';
  /** Omitted for historical image blocks. */
  contentVariant?: 'text' | 'image';
  /** Authored information text, optionally displayed alongside an image or GIF. */
  body?: string;
  /** Omitted preserves historical reaction lifecycle; new content is passive. */
  responseMode?: 'passive' | 'legacy_reaction';
  /** Call delivery; absent means Spoken. Both modes also display the content. */
  delivery?: 'spoken' | 'on_screen';
  /**
   * R2 object key for the uploaded image. Source of truth for cleanup,
   * captioning, and the publish-gate validation. Not shown to the respondent.
   */
  imageStorageKey?: string;
  /** Where the image is delivered from. Omitted historical values are uploads. */
  imageSource?: 'upload' | 'klipy';
  /**
   * Relative public serve URL embedded in the respondent UI and the live mount
   * message (`/api/workspace-assets/{workspaceId}/info-blocks/{filename}`).
   * Resolves against the app origin. Rendered to the respondent.
   */
  imageUrl?: string;
  /** Static counterpart used to pause a remotely hosted animation. */
  imageStillUrl?: string;
  /** Validated image MIME type. */
  mimeType?: string;
  /** Stable KLIPY content id for a remotely hosted animation. */
  gifId?: string;
  /** Stable KLIPY slug used to register a selection with the provider. */
  gifSlug?: string;
  /**
   * Short 2-5 word headline: how hollie names the image to the respondent. An
   * author-typed value is never overwritten by the AI caption pipeline.
   */
  title?: string;
  /**
   * One sentence telling the respondent what to do with the image. The cue that
   * makes hollie call the show tool. An author-typed value is never overwritten.
   */
  directive?: string;
  /**
   * Rich AI visual description: hollie's text surrogate for an image she never
   * receives. Not shown to the respondent. Load-bearing realtime data, gated at
   * publish. The author never writes this; the caption pipeline always sets it.
   */
  caption?: string;
}

/**
 * Calendar block: hollie checks a connected host calendar live during the
 * conversation and books a follow-up call. Booking constraints are structured
 * fields so a booking never depends on parsing prose.
 */
export interface CalendarBookingFieldConfig {
  type: 'calendar_booking';
  /**
   * Whose calendar hosts the call. Derived by the editor from the picked
   * connection's owner ('self' when it belongs to the viewer), never chosen
   * directly by the author; the worker and validation still read it.
   */
  hostMode: 'self' | 'teammate';
  /** The host's user id (the author's own id when hostMode is 'self'). */
  hostUserId?: string;
  /** The org-scoped calendar_connections row this block books against. */
  connectionId?: string;
  /** Natural-language directive driving hollie's conversational behavior. */
  directive?: string;
  /**
   * Meeting length in minutes: any integer from 5 to 240. The editor clamps
   * authoring to the bound and the worker descriptor re-validates it, falling
   * back to 30 out of range.
   */
  durationMinutes: number;
  /** Free margin required on each side of the booked slot, in minutes. */
  bufferMinutes: number;
  /** Earliest bookable moment, hours from now. */
  minimumNoticeHours: number;
  /** Latest bookable moment, days from now. */
  bookingHorizonDays: number;
  /**
   * The host's weekly availability windows, the single source of truth for when
   * hollie may offer a slot. Defaults to Monday-Friday 09:00-17:00 when absent
   * (see DEFAULT_BOOKABLE_WINDOWS); the author edits them directly.
   */
  bookableWindows?: Array<{
    weekday: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    enabled: boolean;
    start: string;
    end: string;
  }>;
}

/** Single-line free-text input with optional length bounds. */
export interface ShortTextFieldConfig {
  type: 'short_text';
  /** Optional URL format validation, independent of text presentation. */
  textFormat?: 'url';
  /** Display size only; neither variant requires exploration or typing. */
  textPresentation?: 'short' | 'expanded';
  minLength?: number;
  maxLength?: number;
}

/** Numeric input with optional bounds and step. */
export interface NumberFieldConfig {
  type: 'number';
  /** NPS or numeric scale metadata retained in response snapshots. */
  rating?: FormFieldRating;
  min?: number;
  max?: number;
  step?: number;
}

/**
 * Email field. Validation is format-only at this layer; deliverability checks
 * live elsewhere.
 */
export interface EmailFieldConfig {
  type: 'email';
  /** Saves this answer as the guest's email. At most one question per conversation. */
  profileBinding?: GuestProfileBinding;
}

/** A person's name collected as one full-name value, kept exactly as given. */
export interface NameFieldConfig {
  type: 'name';
  /** Collection format. Only the single full-name format exists in this version. */
  nameConfig: NameConfig;
  /** Saves this answer as the guest's name. At most one Name question per conversation. */
  profileBinding?: GuestProfileBinding;
}

/** Versioned Name question format; separate name parts are a later format. */
export interface NameConfig {
  version: 1;
  format: 'full';
}

/**
 * Explicit, versioned mapping from a question to a guest profile property.
 * Email questions map to the guest email and Name questions to the guest name.
 * Runtime uses this saved metadata only, never labels, answer keys or transcript wording.
 */
export interface GuestProfileBinding {
  version: 1;
  subject: 'guest';
  property: 'email' | 'name';
}

/** International phone number, validated and stored with its dialing code. */
export interface PhoneFieldConfig {
  type: 'phone';
}

/** Single-select from a fixed list of options. */
export interface ChoiceSingleFieldConfig {
  type: 'choice_single';
  /** Stable option ID whose selection requires attached free text. */
  otherOptionId?: string;
  /** Visual presentation only; absent or auto adapts to the option count. */
  choicePresentation?: 'auto' | 'list' | 'dropdown';
  /** Provenance for a materialized library; authored options remain the stored snapshot. */
  choiceLibrary?: { id: ChoiceLibraryId; version: string };
  /** Ordered Faces semantics; option values remain the stored answer. */
  rating?: FormFieldRating;
  options: FormFieldOption[];
}

/**
 * Multi-select from a fixed list of options. `minSelected` / `maxSelected`
 * bound how many of `options` may be chosen.
 */
export interface ChoiceMultiFieldConfig {
  type: 'choice_multi';
  /** Stable option ID whose selection requires attached free text. */
  otherOptionId?: string;
  /** Visual presentation only; absent or auto adapts to the option count. */
  choicePresentation?: 'auto' | 'list' | 'dropdown';
  /** Provenance for a materialized library; authored options remain the stored snapshot. */
  choiceLibrary?: { id: ChoiceLibraryId; version: string };
  options: FormFieldOption[];
  minSelected?: number;
  maxSelected?: number;
}

/** Required or optional acknowledgement, optionally linked to authored policy text. */
export interface CheckboxFieldConfig {
  type: 'checkbox';
  checkbox?: { link?: { label: string; url: string } };
}

/** Visual options with explicit single or multiple selection behavior. */
export interface ImageChoiceFieldConfig {
  type: 'image_choice';
  options: FormFieldOption[];
  selectionMode: 'single' | 'multiple';
  minSelected?: number;
  maxSelected?: number;
}

/** Full ordering of a fixed option set. */
export interface RankingFieldConfig {
  type: 'ranking';
  options: FormFieldOption[];
}

/** Inclusive calendar-day offset from today or an earlier date answer. */
export type DateRuleBound =
  | { source: 'today'; offsetDays: number }
  | { source: 'answer'; fieldId: string; offsetDays: number };

/** Ordinary date restrictions. Exceptions never override bounds or blackouts. */
export interface DateQuestionRules {
  /** IANA timezone defining today, independent of the respondent's device. */
  timezone: string;
  /** Sunday is 0, Saturday is 6. Omission allows every weekday. */
  allowedWeekdays?: number[];
  excludedDates?: string[];
  /** Reopen specific weekdays, subject to all other restrictions. */
  includedDates?: string[];
  min?: DateRuleBound;
  max?: DateRuleBound;
}

/** Mode-specific inclusive bounds for a local date/time question. */
export interface DateTimeQuestionConfig {
  mode: 'date' | 'time' | 'date_time';
  min?: string;
  max?: string;
  rules?: DateQuestionRules;
}

/** Local date, local time, or timezone-qualified local date-time. */
export interface DateTimeFieldConfig {
  type: 'date_time';
  dateTime: DateTimeQuestionConfig;
}

/** One or multiple column selections for every configured matrix row. */
export interface MatrixFieldConfig {
  type: 'matrix';
  /** Missing mode preserves the original one-answer-per-row format. */
  selectionMode?: 'single' | 'multiple';
  matrix: { rows: FormFieldMatrixItem[]; columns: FormFieldMatrixItem[] };
}

/** Structured postal-address collection settings. */
export interface AddressFieldConfig {
  type: 'address';
  address: { requirePostalCode: boolean };
}

/** Typed-name or drawn signature collection. */
export interface SignatureFieldConfig {
  type: 'signature';
}

/** One or more normalized respondent clicks on an uploaded image. */
export interface ImageClickMapFieldConfig {
  type: 'image_click_map';
  image?: QuestionImage;
  minClicks?: number;
  maxClicks?: number;
}

/** Device files collected into the conversation's customer-owned destination. */
export interface FileUploadFieldConfig {
  type: 'file_upload';
  fileConstraints: FileCollectionConstraints;
}

/**
 * Discriminated union of every per-type config. Switching on `config.type`
 * (or equivalently `field.type` once intersected with `FormFieldBase`) gives
 * full TS narrowing.
 */
export type FormFieldConfig =
  | FreeFormExploreFieldConfig
  | InformationBlockFieldConfig
  | CalendarBookingFieldConfig
  | FileUploadFieldConfig
  | ShortTextFieldConfig
  | NumberFieldConfig
  | EmailFieldConfig
  | PhoneFieldConfig
  | NameFieldConfig
  | ChoiceSingleFieldConfig
  | ChoiceMultiFieldConfig
  | CheckboxFieldConfig
  | ImageChoiceFieldConfig
  | RankingFieldConfig
  | DateTimeFieldConfig
  | MatrixFieldConfig
  | AddressFieldConfig
  | SignatureFieldConfig
  | ImageClickMapFieldConfig;

/**
 * The complete, narrowable shape of a single form field. Compose by
 * intersection so the `type` discriminator and the type-specific config sit
 * at the same level as `FormFieldBase`'s common fields.
 *
 * Example narrowing:
 * ```ts
 * if (field.type === 'choice_multi') {
 *   field.options // typed as FormFieldOption[]
 * }
 * ```
 */
export type FormField = FormFieldBase & FormFieldConfig;

/** Pre-fill data for personalized invite links */
export interface InvitePrefillData {
  /** Interviewee full name */
  name?: string;
  /** Interviewee email */
  email?: string;
  /** Interviewee job title */
  job?: string;
  /** Interviewee company name */
  company?: string;
  /** Interviewee LinkedIn profile URL */
  linkedin?: string;
  /** Interviewee company website URL */
  company_url?: string;
  /** Arbitrary metadata key-value pairs from the admin */
  metadata?: Record<string, string>;
}
